/**
 * TenderFilterChips — reusable filter row matching the search-dashboard
 * chip pattern (status select, category multi-select, date range,
 * optional members select, clear-all).
 *
 * Controlled component — parent owns `filters` and supplies `onChange`.
 * Companion helper `applyTenderFilters` (in utils/) does the actual
 * filtering on a client-side array.
 */
import React from 'react';
import { Activity, Users, X } from 'lucide-react';
import CategoryFilter from './CategoryFilter';
import DateRangeFilter from './DateRangeFilter';
import { FILTER_DEFAULTS, hasActiveFilters } from '../utils/applyTenderFilters';

const STATUS_OPTIONS = [
  { value: 'all',           label: 'All status' },
  { value: 'open',          label: 'Open' },
  { value: 'closing_soon',  label: 'Closing soon' },
  { value: 'closed',        label: 'Closed' },
];

const TenderFilterChips = ({
  filters,
  onChange,
  members = null,        // null = hide member chip; [] = show w/ no options; ['Alice','Bob'] = active list
  memberLabel = 'Saved by',
  resultsCount = null,   // optional inline count display
  resultsLabel = 'item',
}) => {
  const f = { ...FILTER_DEFAULTS, ...(filters || {}) };

  const update = (patch) => onChange({ ...f, ...patch });
  const clearAll = () => onChange({ ...FILTER_DEFAULTS });

  return (
    <div className="compact-filters">
      {resultsCount !== null && (
        <span className="results-count">
          <strong>{resultsCount.toLocaleString()}</strong> {resultsLabel}{resultsCount !== 1 ? 's' : ''}
        </span>
      )}

      {/* Status */}
      <div className="filter-chip" title="Status">
        <Activity size={14} />
        <select
          aria-label="Status"
          className="filter-chip-select"
          value={f.status}
          onChange={(e) => update({ status: e.target.value })}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Categories — popover multiselect (has its own chip-style button) */}
      <CategoryFilter
        selected={f.categories}
        onApply={(cats) => update({ categories: cats })}
      />

      {/* Date range — published + closing (has its own chip-style button) */}
      <DateRangeFilter
        fromDate={f.fromDate}
        toDate={f.toDate}
        closingFrom={f.closingFrom}
        closingTo={f.closingTo}
        onApply={({ from_date, to_date, closing_from, closing_to }) =>
          update({
            fromDate: from_date || null,
            toDate: to_date || null,
            closingFrom: closing_from || null,
            closingTo: closing_to || null,
          })}
      />

      {/* Member — only renders when caller provides options */}
      {Array.isArray(members) && (
        <div className="filter-chip" title={memberLabel}>
          <Users size={14} />
          <select
            aria-label={memberLabel}
            className="filter-chip-select"
            value={f.member}
            onChange={(e) => update({ member: e.target.value })}
          >
            <option value="all">{`${memberLabel} · all (${members.length})`}</option>
            {members.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      )}

      {hasActiveFilters(f) && (
        <button
          type="button"
          className="filter-chip filter-chip-clear"
          onClick={clearAll}
          title="Clear all filters"
        >
          <X size={14} /> Clear all
        </button>
      )}
    </div>
  );
};

export default TenderFilterChips;
